import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Loader2, AlertCircle, X, RotateCcw, FlaskConical, Package } from "lucide-react";

// Inline editor for one test/package row. Mirrors the backend rules
// (lab_test_serializers.VendorTestEditSerializer + vendor_test_edit.update_test):
// name required, MRP / AyushPay price > 0, price <= MRP. Sends only changed
// fields plus `updated_at` so a concurrent edit is detected, not overwritten.

const toForm = (t) => ({
  name: t.name || "",
  type: t.type || "test",
  category: t.category || "",
  description: t.description || "",
  mrp: t.mrp != null ? String(Number(t.mrp)) : "",
  ayushpay_price: t.ayushpay_price != null ? String(Number(t.ayushpay_price)) : "",
  tat_in_hours: t.tat_in_hours != null ? String(t.tat_in_hours) : "",
  fasting_required: !!t.fasting_required,
  home_collection: !!t.home_collection,
  walk_in: !!t.walk_in,
  aliases: Array.isArray(t.aliases) ? t.aliases : [],
});

const money = (v) => (v === "" || Number.isNaN(Number(v)) ? null : Number(v));
const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

export const buildPayload = (original, form) => {
  const base = toForm(original);
  const payload = {};
  if (form.name.trim() !== base.name) payload.name = form.name.trim();
  if (form.type !== base.type) payload.type = form.type;
  if (form.category.trim() !== base.category) payload.category = form.category.trim() || null;
  if (form.description.trim() !== base.description) payload.description = form.description.trim() || null;
  if (money(form.mrp) !== money(base.mrp)) payload.mrp = Number(form.mrp).toFixed(2);
  if (money(form.ayushpay_price) !== money(base.ayushpay_price)) payload.ayushpay_price = Number(form.ayushpay_price).toFixed(2);
  if (form.tat_in_hours !== base.tat_in_hours) payload.tat_in_hours = form.tat_in_hours === "" ? null : Number(form.tat_in_hours);
  ["fasting_required", "home_collection", "walk_in"].forEach((k) => {
    if (form[k] !== base[k]) payload[k] = form[k];
  });
  if (JSON.stringify(form.aliases) !== JSON.stringify(base.aliases)) payload.aliases = form.aliases;
  return payload;
};

export const validateForm = (form) => {
  const errors = {};
  if (!form.name.trim()) errors.name = "Name is required.";
  const mrp = money(form.mrp);
  const price = money(form.ayushpay_price);
  if (mrp == null || mrp <= 0) errors.mrp = "Enter an MRP above ₹0.";
  if (price == null || price <= 0) errors.ayushpay_price = "Enter a price above ₹0.";
  else if (mrp != null && price > mrp) errors.ayushpay_price = "Can't be more than the MRP.";
  if (form.tat_in_hours !== "" && (!/^\d+$/.test(form.tat_in_hours) || Number(form.tat_in_hours) > 8760)) {
    errors.tat_in_hours = "Whole hours, up to 8760.";
  }
  return errors;
};

const Label = ({ children, htmlFor }) => (
  <label htmlFor={htmlFor} className="block text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-1.5">{children}</label>
);

const FieldError = ({ children }) =>
  children ? <p className="mt-1 flex items-center gap-1 text-[11px] text-red-600"><AlertCircle className="w-3 h-3" /> {children}</p> : null;

const inputCls = (err) =>
  `w-full px-3 py-2 rounded-lg border text-sm bg-white transition-colors focus:outline-none focus:ring-2 ${
    err ? "border-red-300 focus:ring-red-200" : "border-gray-200 focus:ring-[#00A6A6]/40 focus:border-[#00A6A6]"
  }`;

const Toggle = ({ label, checked, onChange, disabled }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={() => onChange(!checked)}
    className={`flex items-center justify-between gap-3 w-full px-3 py-2 rounded-lg border text-sm transition-colors ${
      checked ? "border-[#00A6A6]/40 bg-[#F0FAFA] text-gray-900" : "border-gray-200 bg-white text-gray-600"
    } disabled:opacity-60`}
  >
    <span>{label}</span>
    <span className={`relative w-8 h-[18px] rounded-full transition-colors ${checked ? "bg-[#00A6A6]" : "bg-gray-300"}`}>
      <motion.span layout className="absolute top-[2px] w-[14px] h-[14px] rounded-full bg-white shadow"
        style={{ left: checked ? 16 : 2 }} transition={{ type: "spring", stiffness: 500, damping: 32 }} />
    </span>
  </button>
);

const AliasInput = ({ value, onChange, disabled }) => {
  const [draft, setDraft] = useState("");
  const add = (raw) => {
    const parts = raw.split(",").map((p) => p.trim()).filter(Boolean); // ok: trims user-typed alias fragments
    if (!parts.length) return;
    const lower = new Set(value.map((a) => a.toLowerCase()));
    onChange([...value, ...parts.filter((p) => !lower.has(p.toLowerCase()))].slice(0, 50)); // ok: de-dupes user input
    setDraft("");
  };
  return (
    <div className={`flex flex-wrap items-center gap-1.5 px-2 py-1.5 min-h-[40px] rounded-lg border border-gray-200 bg-white focus-within:ring-2 focus-within:ring-[#00A6A6]/40 focus-within:border-[#00A6A6] ${disabled ? "opacity-60" : ""}`}>
      {value.map((alias) => (
        <span key={alias} className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-md bg-[#EAF2FA] text-[#0F4C81] text-xs">
          {alias}
          <button type="button" disabled={disabled} aria-label={`Remove ${alias}`}
            onClick={() => onChange(value.filter((a) => a !== alias))} // ok: removes one alias chip
            className="p-0.5 rounded hover:bg-white/70"><X className="w-3 h-3" /></button>
        </span>
      ))}
      <input
        value={draft}
        disabled={disabled}
        onChange={(e) => (e.target.value.endsWith(",") ? add(e.target.value) : setDraft(e.target.value))}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); add(draft); }
          if (e.key === "Backspace" && !draft && value.length) onChange(value.slice(0, -1));
        }}
        onBlur={() => add(draft)}
        placeholder={value.length ? "Add another…" : "e.g. CBC, Hemogram — press Enter"}
        className="flex-1 min-w-[140px] px-1 py-1 text-sm bg-transparent focus:outline-none"
      />
    </div>
  );
};

const TestEditForm = ({ test, onCancel, onSave, scopeNote, onDirtyChange }) => {
  const [form, setForm] = useState(() => toForm(test));
  const [touched, setTouched] = useState({});
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState(null); // { message, code }
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const nameRef = useRef(null);

  useEffect(() => { nameRef.current?.focus(); }, []);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const errors = useMemo(() => validateForm(form), [form]);
  const payload = useMemo(() => buildPayload(test, form), [test, form]);
  const dirty = Object.keys(payload).length > 0;
  const valid = Object.keys(errors).length === 0;
  const show = (k) => (touched[k] ? errors[k] : null);

  useEffect(() => { onDirtyChange?.(dirty); }, [dirty, onDirtyChange]);

  const mrp = money(form.mrp);
  const price = money(form.ayushpay_price);
  const saving_amount = mrp && price && price <= mrp ? mrp - price : 0;
  const pct = mrp && saving_amount ? Math.round((saving_amount / mrp) * 100) : 0;

  const submit = async () => {
    setTouched({ name: true, mrp: true, ayushpay_price: true, tat_in_hours: true });
    if (!valid || !dirty || saving) return;
    setSaving(true);
    setServerError(null);
    try {
      await onSave({ ...payload, updated_at: test.updated_at });
    } catch (err) {
      setServerError({ message: err.message || "Couldn't save this test.", code: err.code || null });
      setSaving(false);
    }
  };

  const cancel = () => (dirty ? setConfirmDiscard(true) : onCancel());

  const onKeyDown = (e) => {
    if (e.key === "Escape") { e.stopPropagation(); if (confirmDiscard) setConfirmDiscard(false); else cancel(); }
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); submit(); }
  };

  const TypeIcon = form.type === "package" ? Package : FlaskConical;

  return (
    <motion.form
      layout
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      onSubmit={(e) => { e.preventDefault(); submit(); }}
      onKeyDown={onKeyDown}
      className="bg-white rounded-xl ring-2 ring-[#00A6A6]/35 shadow-[0_10px_30px_-14px_rgba(0,166,166,0.45)] overflow-hidden"
    >
      <div className="px-4 py-3 border-b border-[#E9F2F2] bg-gradient-to-r from-[#F3FBFB] to-white flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${form.type === "package" ? "bg-purple-50 text-purple-600" : "bg-blue-50 text-blue-600"}`}>
            <TypeIcon className="w-3.5 h-3.5" />
          </span>
          <p className="text-sm font-semibold text-gray-900 truncate">Edit {form.type === "package" ? "package" : "test"}</p>
        </div>
        {dirty && (
          <button type="button" onClick={() => { setForm(toForm(test)); setTouched({}); setServerError(null); }}
            className="inline-flex items-center gap-1 text-[11px] font-medium text-gray-500 hover:text-gray-800">
            <RotateCcw className="w-3 h-3" /> Reset
          </button>
        )}
      </div>

      <div className="p-4 space-y-5">
        {serverError && (
          <div className="flex items-start gap-2 rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-xs text-red-700">
            <AlertCircle className="w-3.5 h-3.5 mt-px shrink-0" />
            <span>{serverError.message}</span>
          </div>
        )}

        <section className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3">
          <div>
            <Label htmlFor="t-name">Name</Label>
            <input id="t-name" ref={nameRef} value={form.name} maxLength={255}
              onChange={(e) => set("name", e.target.value)} onBlur={() => setTouched((t) => ({ ...t, name: true }))}
              className={inputCls(show("name"))} />
            <FieldError>{show("name")}</FieldError>
          </div>
          <div>
            <Label>Type</Label>
            <div className="inline-flex p-1 rounded-lg bg-gray-100">
              {["test", "package"].map((t) => (
                <button key={t} type="button" onClick={() => set("type", t)}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium capitalize transition-all ${form.type === t ? "bg-white text-[#0F4C81] shadow-sm" : "text-gray-500 hover:text-gray-700"}`}>
                  {t}
                </button>
              ))}
            </div>
          </div>
        </section>

        <section className="rounded-xl bg-[#F7FBFB] ring-1 ring-[#E3EEEE] p-3">
          <div className="grid grid-cols-2 gap-3">
            {[["mrp", "MRP"], ["ayushpay_price", "AyushPay price"]].map(([key, label]) => (
              <div key={key}>
                <Label htmlFor={`t-${key}`}>{label}</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400">₹</span>
                  <input id={`t-${key}`} inputMode="decimal" value={form[key]}
                    onChange={(e) => set(key, e.target.value.replace(/[^\d.]/g, "").replace(/(\..*)\./g, "$1"))}
                    onBlur={() => setTouched((t) => ({ ...t, [key]: true }))}
                    className={`${inputCls(show(key))} pl-7 tabular-nums`} />
                </div>
                <FieldError>{show(key)}</FieldError>
              </div>
            ))}
          </div>
          <p className="mt-2.5 text-xs text-gray-500">
            {saving_amount > 0 ? (
              <>Customers save <span className="font-semibold text-emerald-600">{inr(saving_amount)}</span>
                <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold">{pct}% off</span></>
            ) : price && mrp && price === mrp ? "No discount — AyushPay price equals MRP." : "Set both prices to preview the discount."}
          </p>
        </section>

        <section className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label htmlFor="t-category">Category</Label>
            <input id="t-category" value={form.category} maxLength={255} placeholder="e.g. hematology"
              onChange={(e) => set("category", e.target.value)} className={inputCls(false)} />
          </div>
          <div>
            <Label htmlFor="t-tat">Report TAT (hours)</Label>
            <input id="t-tat" inputMode="numeric" value={form.tat_in_hours} placeholder="e.g. 24"
              onChange={(e) => set("tat_in_hours", e.target.value.replace(/\D/g, "").slice(0, 4))}
              onBlur={() => setTouched((t) => ({ ...t, tat_in_hours: true }))}
              className={inputCls(show("tat_in_hours"))} />
            <FieldError>{show("tat_in_hours")}</FieldError>
          </div>
        </section>

        <section className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <Toggle label="Fasting required" checked={form.fasting_required} onChange={(v) => set("fasting_required", v)} />
          <Toggle label="Home collection" checked={form.home_collection} onChange={(v) => set("home_collection", v)} />
          <Toggle label="Walk-in" checked={form.walk_in} onChange={(v) => set("walk_in", v)} />
        </section>

        <section>
          <Label>Search aliases</Label>
          <AliasInput value={form.aliases} onChange={(v) => set("aliases", v)} />
        </section>

        <section>
          <Label htmlFor="t-desc">Description</Label>
          <textarea id="t-desc" rows={2} value={form.description} onChange={(e) => set("description", e.target.value)}
            className={`${inputCls(false)} resize-y min-h-[60px]`} />
        </section>
      </div>

      {confirmDiscard ? (
        <div className="px-4 py-3 border-t border-amber-200 bg-amber-50 flex flex-wrap items-center justify-between gap-3">
          <p className="text-xs text-amber-800">Discard your changes to this {form.type}?</p>
          <div className="flex gap-2">
            <button type="button" onClick={() => setConfirmDiscard(false)}
              className="px-3 py-1.5 rounded-lg text-sm font-medium text-gray-700 hover:bg-white">Keep editing</button>
            <button type="button" onClick={() => onCancel()}
              className="px-3 py-1.5 rounded-lg text-sm font-medium text-white bg-amber-600 hover:bg-amber-700">Discard</button>
          </div>
        </div>
      ) : (
      <div className="px-4 py-3 border-t border-[#E9F2F2] bg-[#FAFDFD] flex flex-wrap items-center justify-between gap-3">
        <p className="text-[11px] text-gray-500 max-w-[60%]">
          {scopeNote}
          <span className="hidden sm:inline text-gray-400"> · Esc to cancel · ⌘/Ctrl+Enter to save</span>
        </p>
        <div className="flex gap-2">
          <button type="button" onClick={cancel} disabled={saving}
            className="px-3 py-1.5 rounded-lg text-sm font-medium text-gray-700 border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-50">
            Cancel
          </button>
          <button type="submit" disabled={saving || !dirty || !valid}
            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-sm font-medium text-white bg-[#0F4C81] hover:bg-[#0C3E6B] disabled:opacity-50 disabled:cursor-not-allowed">
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {saving ? "Saving…" : dirty ? "Save changes" : "No changes"}
          </button>
        </div>
      </div>
      )}
    </motion.form>
  );
};

export default TestEditForm;
